import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { ChevronDown, ChevronUp, Eye } from "lucide-react";

import { stripBundlePrefix } from "@/utils/bundle-name";
import { cn } from "@/utils/ui";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import {
  DeleteFileConfirmation,
  RegenerateFileConfirmation,
} from "@components/FilesListDataTable";
import { Button } from "@components/ui/button";
import { ScrollArea, ScrollBar } from "@components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { formatNullableDateToLocal } from "@utils/dateUtils";

import { formatDocumentCategory } from "../utils/classificationMapper";

type DocumentTimelineViewProps = {
  files: DocSorterFileSchema[];
  onViewFile: (file: DocSorterFileSchema | null) => void;
  onViewSummary: (file: DocSorterFileSchema) => void;
  isLoading: boolean;
  selectedDocumentId?: string;
  showSummaryPanel?: boolean;
  caseId?: string;
  initialScrollTopRef?: React.MutableRefObject<number>;
  onScrollTopChange?: (top: number) => void;
  // MDRS-420: doctors (expert-view users) can't delete files — defaults to
  // true so callers that don't pass it (most existing tests) keep today's
  // behaviour.
  canManageFiles?: boolean;
};

export const DocumentTimelineView = ({
  files,
  onViewFile,
  onViewSummary,
  isLoading,
  selectedDocumentId,
  showSummaryPanel,
  initialScrollTopRef,
  onScrollTopChange,
  canManageFiles = true,
}: DocumentTimelineViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const firstDotRef = useRef<HTMLButtonElement>(null);
  const lastDotRef = useRef<HTMLButtonElement>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const hasAppliedInitialScrollRef = useRef(false);

  // MDRS-408: "View Summary" remounts this view (the page swaps its layout
  // tree), which would otherwise reset scroll to the top. Apply the
  // caller-supplied offset once the scroll area actually exists — it's
  // gated behind the loading/empty-state branches above, so it isn't there
  // yet on the first render while files are still loading — and report live
  // scroll position back up so the parent has something to restore next
  // time. `hasAppliedInitialScrollRef` keeps the restore itself one-shot so
  // later re-runs (e.g. the file list changing size) don't yank the user's
  // scroll back to the stale offset.
  useEffect(() => {
    const el = scrollViewportRef.current;
    if (!el) return;
    if (!hasAppliedInitialScrollRef.current && initialScrollTopRef?.current) {
      el.scrollTop = initialScrollTopRef.current;
    }
    hasAppliedInitialScrollRef.current = true;
    const handleScroll = () => onScrollTopChange?.(el.scrollTop);
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, files.length]);

  const toggleExpanded = (fileId: string) => {
    setExpandedItems((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    );
  };

  const sortedFiles = [...files].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    return dateB - dateA; // Reverse to get most recent first
  });

  // The center connector previously ran the full height of the <ol> (top-0
  // to bottom-0). That overshoots on both ends: above the first dot (the
  // <ol>'s own top padding sits above where the first dot actually renders)
  // and below the last dot (the last card's content extends well past its
  // own dot). Measure both dots' actual positions instead so the line spans
  // exactly first-dot-center to last-dot-center. Card heights vary with
  // content and expand/collapse, so this re-measures on every render via a
  // layout effect (cheap: a couple of getBoundingClientRect reads) and on
  // any resize/reflow the ResizeObserver below catches (font load, window
  // resize, etc).
  useLayoutEffect(() => {
    const syncLine = () => {
      const line = lineRef.current;
      const firstDot = firstDotRef.current;
      const lastDot = lastDotRef.current;
      const ol = line?.parentElement;
      if (!line || !firstDot || !lastDot || !ol) return;

      // getBoundingClientRect, not offsetTop — each dot's offsetParent is
      // its own <li> (the nearest positioned ancestor), not the <ol> the
      // line is positioned against, so offsetTop alone wouldn't give a
      // comparable value.
      const firstDotRect = firstDot.getBoundingClientRect();
      const lastDotRect = lastDot.getBoundingClientRect();
      const olRect = ol.getBoundingClientRect();

      const top = firstDotRect.top + firstDotRect.height / 2 - olRect.top;
      const bottom = lastDotRect.top + lastDotRect.height / 2 - olRect.top;

      line.style.top = `${top}px`;
      line.style.height = `${bottom - top}px`;
    };

    syncLine();

    const observer = new ResizeObserver(syncLine);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <LoadingIcon className="mx-auto mb-4 size-8 animate-spin-ccw" />
          <p className="text-sm text-foreground-muted">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <p className="text-sm text-foreground-muted">No documents found</p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea ref={scrollViewportRef} className="h-full">
      <section ref={containerRef}>
        <ol className="relative flex flex-col items-center px-6 pb-3 pt-6">
          {/* Center timeline line — top/height are measured (see
              useLayoutEffect above) to span exactly first-dot to last-dot,
              not the <ol>'s own padded box. */}
          <div
            ref={lineRef}
            className="absolute left-1/2 -ml-[1px] w-[2px] bg-gray-200"
            aria-hidden="true"
          ></div>

          {sortedFiles.map((file, index) => {
            const isSelected = selectedDocumentId === file.id;
            const isExpanded = expandedItems.includes(file.id);
            const isOnLeftSide = index % 2 === 1;
            const hasNoSummary = !file.short_summary;

            return (
              <li
                key={file.id}
                className={cn(
                  "relative flex w-full",
                  index === 0 ? "mb-6" : "-mt-16",
                  hasNoSummary && "-mt-10"
                )}
              >
                {/* Timeline dot */}
                <button
                  ref={(el) => {
                    // A single-item list is both first and last — set
                    // whichever refs apply rather than picking one.
                    if (index === 0) firstDotRef.current = el;
                    if (index === sortedFiles.length - 1) lastDotRef.current = el;
                  }}
                  type="button"
                  aria-label={isSelected ? "Deselect document" : "Select document"}
                  className={cn(
                    "absolute left-1/2 z-10 -ml-[10px] h-5 w-5 rounded-full",
                    isSelected
                      ? "border-primary bg-primary"
                      : "border-2 border-border bg-background-white"
                  )}
                  onClick={() => onViewFile(isSelected ? null : file)}
                />

                {/* Connecting line from dot to item. The article is 47%
                    wide and pushed to one edge, leaving a 3% gap to the
                    center — this line must match that percentage (not a
                    fixed px width) or it over/undershoots the card edge at
                    narrower container widths. */}
                {!showSummaryPanel && (
                  <div
                    className={cn(
                      "z-5 absolute top-[10px] h-[1px] w-[3%]",
                      isSelected ? "bg-primary" : "bg-gray-200",
                      isOnLeftSide ? "left-[47%]" : "left-1/2"
                    )}
                    aria-hidden="true"
                  />
                )}

                <article
                  className={cn(
                    "relative z-10 -mt-2 cursor-pointer rounded-[20px] border",
                    isOnLeftSide ? "mr-auto pr-2" : "ml-auto pl-2",
                    "w-[47%]",
                    isSelected ? "border-primary shadow-md" : "border-gray-200"
                  )}
                  onClick={() => onViewFile(isSelected ? null : file)}
                >
                  <div className="p-3">
                    {/* Document header */}
                    <header className="flex items-center">
                      <time
                        className="font-medium text-gray-700"
                        dateTime={file.date || ""}
                      >
                        {formatNullableDateToLocal(file.date, "dd/MM/yyyy")}
                      </time>
                    </header>

                    {/* Document body */}
                    <div className="mt-2">
                      {/* Document description */}
                      {file.short_summary ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p
                              className={cn(
                                "font-head text-[13px] text-gray-800",
                                !isExpanded && "line-clamp-2 cursor-help"
                              )}
                            >
                              {file.short_summary}
                            </p>
                          </TooltipTrigger>
                          {!isExpanded && (
                            <TooltipContent className="max-w-md">
                              <p className="font-head text-[13px]">
                                {file.short_summary}
                              </p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      ) : (
                        <p className="font-head text-[13px] italic text-gray-800">
                          No description available
                        </p>
                      )}

                      {/* Category */}
                      <dl className="mt-2">
                        <div className="flex items-center text-xs">
                          <dt className="text-gray-500">Category:</dt>
                          <dd className="ml-1 break-words font-medium text-gray-700">
                            {formatDocumentCategory(file.classification)}
                          </dd>
                        </div>
                      </dl>

                      {/* Toggle expand button */}
                      <Button
                        variant="link"
                        size="sm"
                        className="mt-2 h-auto p-0 text-xs font-medium text-primary"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent container click
                          toggleExpanded(file.id);
                        }}
                      >
                        {isExpanded ? (
                          <>
                            See less{" "}
                            <ChevronUp className="ml-1 h-3 w-3" aria-hidden="true" />
                          </>
                        ) : (
                          <>
                            See more{" "}
                            <ChevronDown
                              className="ml-1 h-3 w-3"
                              aria-hidden="true"
                            />
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Expanded content - file details */}
                    {isExpanded && (
                      <div className="mt-2 border-t border-border pt-2">
                        <dl className="space-y-1 text-xs">
                          <div className="flex">
                            <dt className="w-20 text-gray-500">File name:</dt>
                            <dd
                              className="truncate font-medium text-gray-700"
                              title={stripBundlePrefix(file.name)}
                            >
                              {stripBundlePrefix(file.name)}
                            </dd>
                          </div>
                          <div className="flex">
                            <dt className="w-20 text-gray-500">Uploaded date:</dt>
                            <dd className="font-medium text-gray-700">
                              {formatNullableDateToLocal(
                                file.created_at,
                                "dd/MM/yyyy"
                              )}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="mt-3 flex space-x-1 border-t border-border pt-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent container click
                              onViewSummary(file);
                            }}
                            className="h-8 w-8 text-secondary"
                          >
                            <Eye className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">View Full Summary</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>View Full Summary</TooltipContent>
                      </Tooltip>

                      <RegenerateFileConfirmation
                        file={{
                          id: file.id,
                          name: file.name,
                          classification: file.classification || "",
                          date: file.date || undefined,
                          case: file.case,
                        }}
                      />

                      {canManageFiles && (
                        <DeleteFileConfirmation
                          file={{
                            id: file.id,
                            name: file.name,
                            classification: file.classification || "",
                            date: file.date || undefined,
                            case: file.case,
                          }}
                          iconOnly
                        />
                      )}
                    </div>
                  </div>
                </article>
              </li>
            );
          })}
        </ol>
      </section>
      <ScrollBar orientation="vertical" />
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};
